import { Fragment, useMemo, useRef, useState } from 'react';
import { nextDirection, sortRows } from './sorting.js';

const words = { asc: 'ascending', desc: 'descending' };

/**
 * Click-to-sort for a staff table. `columns` is `{ key: { value: (row) => anything, first: 'asc' | 'desc' } }`: `value` is what
 * the column sorts by (the data behind the cell, not its words), and `first` is the direction of its first click ('asc' for
 * text, 'desc' for numbers, money and dates). Until a header is clicked the rows stay in the order they came in.
 *
 * Returns the rows in order, and `header(key, label)`, which draws the `<th>` for that column: a button that sorts by it,
 * then reverses on each click, showing ▲ or ▼ while it is the sorted column. Call it before any early return in the component.
 */
export function useSort(rows, columns) {
  const [sort, setSort] = useState(null);
  // The newest column definitions, without sorting again every time a screen redraws just because it built them afresh.
  const latest = useRef(columns);
  latest.current = columns;

  const sorted = useMemo(() => {
    const column = sort && latest.current[sort.key];
    return column ? sortRows(rows, column.value, sort.direction) : rows;
  }, [rows, sort]);

  const header = (key, label) => {
    const direction = sort?.key === key ? sort.direction : null;
    const click = () => setSort((current) => ({ key, direction: nextDirection(current, key, latest.current[key]?.first) }));
    return (
      <th key={key} aria-sort={direction ? words[direction] : 'none'}>
        <button type="button" className="admin-sort" onClick={click}>
          {label}
          {direction && <span className="admin-sort-arrow" aria-hidden="true">{direction === 'asc' ? '▲' : '▼'}</span>}
        </button>
      </th>
    );
  };

  return { rows: sorted, header, sort };
}

/**
 * A whole table with every column sortable. Each column is `{ key, label, value, first, cell }`: `value` and `first` as for
 * `useSort`, and `cell(row)` what is shown in that column. `rowKey(row, index)` names a row.
 * Optionally, `rowProps(row)` gives a row extra attributes (a class, a click handler), and `detail(row)` gives whatever opens
 * in place under that row, full width (nothing when it is closed). A detail stays under its row when the table is sorted.
 */
export function SortableTable({ columns, rows, rowKey, rowProps, detail }) {
  const { rows: sorted, header } = useSort(rows, Object.fromEntries(columns.map(({ key, value, first }) => [key, { value, first }])));
  return (
    <div className="mod-table-wrap">
      <table className="mod-audit">
        <thead><tr>{columns.map((column) => header(column.key, column.label))}</tr></thead>
        <tbody>
          {sorted.map((row, index) => {
            const open = detail?.(row);
            return (
              <Fragment key={rowKey(row, index)}>
                <tr {...rowProps?.(row)}>
                  {columns.map((column) => <td key={column.key}>{column.cell(row)}</td>)}
                </tr>
                {open && <tr className="admin-email-open"><td colSpan={columns.length}>{open}</td></tr>}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
